import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { doc, getDoc, Firestore } from '@angular/fire/firestore';
import { from, of, switchMap, take } from 'rxjs';
import { ClinicService } from '../services/clinic.service';
import { AuthService } from '../services/auth.service';
import { Clinic } from '../models/clinic.model';

export const authGuard: CanActivateFn = () => {
  const router = inject(Router);
  const clinicService = inject(ClinicService);
  const firestore = inject(Firestore);

  if (clinicService.clinic()) return true;

  // Restore impersonation context after a page refresh
  if (clinicService.restoreImpersonation()) return true;

  return user(inject(Auth)).pipe(
    take(1),
    switchMap((u) => {
      if (!u) return of(router.createUrlTree(['/login']));
      return from(getDoc(doc(firestore, 'userClinics', u.uid))).pipe(
        switchMap(async (ucSnap) => {
          if (!ucSnap.exists()) return router.createUrlTree(['/login']);
          const uc = ucSnap.data() as { clinicId: string; role: string; status: string };
          if (uc.status === 'suspended') {
            inject(AuthService).suspended.set(true);
            return router.createUrlTree(['/login']);
          }
          const clinicSnap = await getDoc(doc(firestore, 'clinics', uc.clinicId));
          if (!clinicSnap.exists()) return router.createUrlTree(['/login']);
          const c = clinicSnap.data() as Clinic;
          clinicService.setClinic({ id: c.id, name: c.name, primaryColor: c.primaryColor, logoUrl: c.logoUrl, trialStartedAt: c.trialStartedAt, subscriptionStatus: c.subscriptionStatus });
          clinicService.setRole(uc.role);
          return true as const;
        }),
      );
    }),
  );
};

export const authenticatedMatch: CanMatchFn = () => {
  if (inject(ClinicService).clinic()) return true;
  return user(inject(Auth)).pipe(
    take(1),
    switchMap((u) => of(!!u)),
  );
};

export const guestGuard: CanActivateFn = () => {
  const router = inject(Router);
  const clinicService = inject(ClinicService);
  const firestore = inject(Firestore);

  return user(inject(Auth)).pipe(
    take(1),
    switchMap((u) => {
      if (!u) return of(true);
      if (clinicService.clinic()) return of(router.createUrlTree(['/']));
      return from(getDoc(doc(firestore, 'userClinics', u.uid))).pipe(
        switchMap((ucSnap) => {
          if (!ucSnap.exists()) return of(true as const);
          const uc = ucSnap.data() as { status: string };
          return of(uc.status !== 'suspended' ? router.createUrlTree(['/']) : (true as const));
        }),
      );
    }),
  );
};
